import React from 'react';
import { Box, CircularProgress as MuiCircularProgress } from '@mui/material';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { NumericField } from 'components/NumericField/NumericField';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { StepDetailsDialogState } from 'features/checklist/hooks/useStepDetails';
import { useCommonModalSlotProps } from 'hooks/useCommonModalSlotProps';
import { useDisableFocusInsideRef } from 'hooks/useDisableFocusInsideRef';

interface StepDetailsConfirmationDialogProps {
  open: boolean;
  dialogState: StepDetailsDialogState;
  handleYes: () => void;
  yesButtonDisabled?: boolean;
  handleNo: () => void;
  stepGoalInput: number;
  setStepGoalInput: (input: number) => void;
  stepCountInput: number;
  setStepCountInput: (input: number) => void;
  /** Overlay area; the Home page edits in its left half, other screens use the full screen. */
  variant?: 'left-half' | 'fullscreen';
}

// Helper functions  to get Dialog props based on the current state

const getDialogTitleKey = (state: StepDetailsDialogState) => {
  switch (state) {
    case StepDetailsDialogState.ERROR:
      return 'misc_error';
    case StepDetailsDialogState.GOAL_EDIT:
      return 'edit_step_goal';
    case StepDetailsDialogState.STEPS_EDIT:
      return 'update_today_count_title';
    case StepDetailsDialogState.GOAL_SAVED:
    case StepDetailsDialogState.STEPS_SAVED:
      return 'new_step_goal_saved';
    default:
      return '';
  }
};

const getDialogDescriptionKey = (state: StepDetailsDialogState) => {
  switch (state) {
    case StepDetailsDialogState.ERROR:
      return 'error_loading_body';
    case StepDetailsDialogState.GOAL_SAVED:
      return 'new_step_goal_start_tomorrow';
    default:
      return '';
  }
};

const getDialogYesButtonLabelKey = (state: StepDetailsDialogState) => {
  switch (state) {
    case StepDetailsDialogState.ERROR:
    case StepDetailsDialogState.GOAL_SAVED:
    case StepDetailsDialogState.STEPS_SAVED:
      return 'general_ok';
    case StepDetailsDialogState.GOAL_EDIT:
    case StepDetailsDialogState.STEPS_EDIT:
      return 'save';
    default:
      return '';
  }
};

const getDialogNoButtonLabelKey = (state: StepDetailsDialogState) => {
  switch (state) {
    case StepDetailsDialogState.GOAL_EDIT:
    case StepDetailsDialogState.STEPS_EDIT:
      return 'cancel';
    default:
      return '';
  }
};

export const StepDetailsConfirmationDialog = ({
  open,
  dialogState,
  handleYes,
  yesButtonDisabled,
  handleNo,
  stepGoalInput,
  setStepGoalInput,
  stepCountInput,
  setStepCountInput,
  variant = 'left-half',
}: StepDetailsConfirmationDialogProps) => {
  const { translation } = useTranslationContext();
  const { slotProps } = useCommonModalSlotProps(variant);
  const { homeLeftSectionRef, taskModalRef } = useLayoutElements();
  const confirmationDialogRef = React.useRef<HTMLDivElement>(null);
  useDisableFocusInsideRef(taskModalRef, !open);

  return (
    <ConfirmationDialog
      ref={confirmationDialogRef}
      open={open}
      title={translation(getDialogTitleKey(dialogState))}
      description={translation(getDialogDescriptionKey(dialogState))}
      yesButtonLabel={translation(getDialogYesButtonLabelKey(dialogState))}
      yesButtonProps={{ disabled: yesButtonDisabled }}
      noButtonLabel={translation(getDialogNoButtonLabelKey(dialogState))}
      handleYes={handleYes}
      handleNo={handleNo}
      slotProps={slotProps}
      // The layout ref is never cleared when Home unmounts (React 18 ignores the cleanup returned
      // by its ref callback), so only trust it on the Home page itself.
      container={variant === 'left-half' ? homeLeftSectionRef.current : undefined}
      aria-live="polite"
      key={dialogState}
    >
      <Box
        width="100%"
        display={dialogState === StepDetailsDialogState.LOADING ? 'flex' : 'none'}
        justifyContent="center"
      >
        <MuiCircularProgress
          aria-label={translation('loading')}
          aria-busy={dialogState === StepDetailsDialogState.LOADING}
        />
      </Box>
      <NumericField
        id="task__step-goal-input"
        autoFocus={dialogState === StepDetailsDialogState.GOAL_EDIT}
        sx={{
          display: dialogState === StepDetailsDialogState.GOAL_EDIT ? 'inherit' : 'none',
        }}
        value={stepGoalInput}
        onChange={(e) => {
          setStepGoalInput(Number(e.target.value));
        }}
        slotProps={{
          htmlInput: {
            maxLength: 5,
          },
        }}
      />
      <NumericField
        id="task__step-count-input"
        autoFocus={dialogState === StepDetailsDialogState.STEPS_EDIT}
        sx={{
          display: dialogState === StepDetailsDialogState.STEPS_EDIT ? 'inherit' : 'none',
        }}
        value={stepCountInput}
        onChange={(e) => {
          setStepCountInput(Number(e.target.value));
        }}
        slotProps={{
          htmlInput: {
            maxLength: 5,
          },
        }}
      />
    </ConfirmationDialog>
  );
};
